import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { API } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, hoy, num, pct } from '../lib/format';
import type { DistribucionLote, EquipoElectrico, Lote, RespuestaCalcular } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { IndicadoresFinancieros } from '../components/IndicadoresFinancieros';
import { useParametros } from '../lib/finanzas';

type FormState = {
  fecha: string;
  kilosMaiz: string;
  arepasBrutas: string;
  arepasMerma: string;
  arepasPorPaquete: string;
  aguaL: string;
  equiposUsados: string[];
  luzMinutos: string;
  gasLb: string;
  bolsas: string;
  personas: string;
  horasTrabajoH: string;
  horasTrabajoMin: string;
  precioVentaPaq: string;
  notas: string;
};

type FormStringKey = {
  [K in keyof FormState]: FormState[K] extends string ? K : never;
}[keyof FormState];

const defaults: FormState = {
  fecha: hoy(),
  kilosMaiz: '3',
  arepasBrutas: '55',
  arepasMerma: '0',
  arepasPorPaquete: '5',
  aguaL: '30',
  equiposUsados: [],
  luzMinutos: '60',
  gasLb: '1',
  bolsas: '15',
  personas: '1',
  horasTrabajoH: '1',
  horasTrabajoMin: '30',
  precioVentaPaq: '2800',
  notas: '',
};

function toNum(v: string): number {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

const STORAGE_KEY = 'produccion:form';
// Si cambiás la forma de FormState, subí esta versión para descartar valores guardados con la forma vieja.
const STORAGE_VERSION = 4;
type StoredForm = FormState & { __v: number };

function cargarInicial(): FormState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<StoredForm>;
    if (parsed.__v !== STORAGE_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return defaults;
    }
    return {
      ...defaults,
      ...parsed,
      fecha: hoy(),
      notas: '',
      equiposUsados: Array.isArray(parsed.equiposUsados) ? parsed.equiposUsados : [],
    };
  } catch {
    return defaults;
  }
}

export function ProduccionPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const { margenObjetivo } = useParametros();
  const [f, setF] = useState<FormState>(cargarInicial);

  useEffect(() => {
    try {
      const toStore: StoredForm = { ...f, __v: STORAGE_VERSION };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch {
      /* noop */
    }
  }, [f]);

  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState<RespuestaCalcular | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  function restablecerFormulario() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
    setF({ ...defaults, fecha: hoy() });
    setResultado(null);
    setError(null);
    setMensaje('Formulario restablecido con los valores por defecto.');
  }

  const qEquipos = useQuery({
    queryKey: ['equipos'],
    queryFn: () => API.get<EquipoElectrico[]>('/equipos'),
    enabled: tiene('equipos:read'),
  });
  const equiposActivos = useMemo(
    () => (qEquipos.data ?? []).filter((e) => e.activo),
    [qEquipos.data],
  );

  const totalWatts = useMemo(
    () =>
      equiposActivos
        .filter((e) => f.equiposUsados.includes(e.id))
        .reduce((sum, e) => sum + Number(e.potenciaW), 0),
    [equiposActivos, f.equiposUsados],
  );
  const luzKWh = (totalWatts * toNum(f.luzMinutos)) / 60 / 1000;

  const payload = useMemo(
    () => ({
      kilosMaiz: toNum(f.kilosMaiz),
      arepasBrutas: Math.round(toNum(f.arepasBrutas)),
      arepasMerma: Math.round(toNum(f.arepasMerma)),
      arepasPorPaquete: Math.max(1, Math.round(toNum(f.arepasPorPaquete))),
      aguaL: toNum(f.aguaL),
      luzKWh,
      gasLb: toNum(f.gasLb),
      bolsas: Math.round(toNum(f.bolsas)),
      personas: Math.round(toNum(f.personas)),
      horasTrabajo: toNum(f.horasTrabajoH) + toNum(f.horasTrabajoMin) / 60,
      precioVentaPaq: toNum(f.precioVentaPaq),
    }),
    [f, luzKWh],
  );

  async function calcular() {
    setError(null);
    setMensaje(null);
    setCalculando(true);
    try {
      const r = await API.post<RespuestaCalcular>('/produccion/calcular', payload);
      setResultado(r);
    } catch (err) {
      setError((err as ApiError).message ?? 'Error al calcular');
    } finally {
      setCalculando(false);
    }
  }

  async function guardarLote() {
    setError(null);
    setMensaje(null);
    setGuardando(true);
    try {
      await API.post<Lote>('/lotes', { ...payload, fecha: f.fecha, notas: f.notas || undefined });
      setMensaje('✓ Lote guardado en Historial de producción.');
      qc.invalidateQueries({ queryKey: ['lotes'] });
      qc.invalidateQueries({ queryKey: ['ultimos-lotes'] });
      qc.invalidateQueries({ queryKey: ['movimientos'] });
      qc.invalidateQueries({ queryKey: ['saldo'] });
    } catch (err) {
      setError((err as ApiError).message ?? 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  function input(name: FormStringKey, label: string, tipo: 'number' | 'text' | 'date' = 'number', unidad?: string) {
    return (
      <div>
        <label className="label">{label}{unidad && <span className="text-slate-400 font-normal"> ({unidad})</span>}</label>
        <input
          type={tipo}
          className="input"
          value={f[name]}
          onChange={(e) => setF((s) => ({ ...s, [name]: e.target.value }))}
          onWheel={tipo === 'number' ? (e) => e.currentTarget.blur() : undefined}
          inputMode={tipo === 'number' ? 'decimal' : undefined}
          step={tipo === 'number' ? 'any' : undefined}
          min={tipo === 'number' ? '0' : undefined}
        />
      </div>
    );
  }

  function toggleEquipo(id: string, checked: boolean) {
    setF((s) => ({
      ...s,
      equiposUsados: checked
        ? [...s.equiposUsados, id]
        : s.equiposUsados.filter((x) => x !== id),
    }));
  }

  const r = resultado?.resultado;
  const precios = resultado?.precios;
  const fijos = resultado?.costosFijos;
  const precioLuz = precios?.luz ?? 0;
  const costoLuzPreview = luzKWh * precioLuz;

  const distribucionSimulada: DistribucionLote | null = r
    ? {
        ingreso: r.ingresoTotal,
        manoObra: r.desglose.manoObraCOP,
        reinversion: r.desglose.maizCOP + r.desglose.gasCOP + r.desglose.bolsasCOP,
        servicios: r.desglose.aguaCOP + r.desglose.luzCOP,
        costosFijos: r.paquetes * r.costoFijoPaq,
        ganancia: r.gananciaTotal,
      }
    : null;

  const loteSimulado: Lote | null = r
    ? ({
        id: 'simulado',
        fecha: f.fecha,
        kilosMaiz: String(payload.kilosMaiz),
        arepasBrutas: payload.arepasBrutas,
        arepasMerma: payload.arepasMerma,
        arepasPorPaquete: payload.arepasPorPaquete,
        aguaL: String(payload.aguaL),
        luzH: '0',
        luzKWh: String(luzKWh),
        gasLb: String(payload.gasLb),
        bolsas: payload.bolsas,
        personas: payload.personas,
        horasTrabajo: String(payload.horasTrabajo),
        precioVentaPaq: String(payload.precioVentaPaq),
        arepasNetas: r.arepasNetas,
        paquetes: r.paquetes,
        costoVariablePaq: String(r.costoVariablePaq),
        costoFijoPaq: String(r.costoFijoPaq),
        costoTotalPaq: String(r.costoTotalPaq),
        costoVariableLote: String(r.costoVariableLote),
        costoFijoLote: String(r.costoFijoLote),
        margenPct: String(r.margenPct),
        gananciaPaq: String(r.gananciaPaq),
        ingresoTotal: String(r.ingresoTotal),
        costoTotalLote: String(r.costoTotalLote),
        gananciaTotal: String(r.gananciaTotal),
        notas: null,
        createdAt: new Date().toISOString(),
      } satisfies Lote)
    : null;

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Calculadora de producción"
        subtitle="Ingresa los datos del lote. Usamos los últimos precios registrados como costos variables."
        action={
          <button
            type="button"
            onClick={restablecerFormulario}
            className="btn-ghost text-sm"
            title="Limpia el formulario y borra el borrador guardado"
          >
            ↺ Restablecer
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* INPUTS */}
        <div className="card p-4 sm:p-5 space-y-5">
          <h2 className="font-semibold">Datos del lote</h2>

          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
            {input('fecha', 'Fecha', 'date')}
            {input('kilosMaiz', 'Kilos de maíz', 'number', 'kg')}
            {input('arepasBrutas', 'Arepas brutas', 'number', 'und')}
            {input('arepasMerma', 'Merma', 'number', 'und')}
            {input('arepasPorPaquete', 'Arepas por paquete', 'number')}
            {input('precioVentaPaq', 'Precio venta', 'number', 'COP/paq')}
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mt-2 mb-2">Insumos consumidos</h3>
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
              {input('gasLb', 'Gas', 'number', 'lb')}
              {input('bolsas', 'Bolsas', 'number', 'und')}
              {input('aguaL', 'Agua', 'number', 'L')}
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mt-2 mb-2">
              Consumo eléctrico
            </h3>
            <p className="text-xs text-slate-500 -mt-1 mb-2">
              Marcá los equipos usados en el lote y los minutos totales que estuvieron corriendo.
            </p>
            {qEquipos.isLoading && (
              <div className="text-sm text-slate-400">Cargando equipos…</div>
            )}
            {!qEquipos.isLoading && equiposActivos.length === 0 && (
              <div className="text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded p-3 text-center">
                No hay equipos activos. Agregá equipos en la página <b>Insumos</b>.
              </div>
            )}
            {equiposActivos.length > 0 && (
              <div className="space-y-1 border border-slate-200 rounded-md divide-y divide-slate-100">
                {equiposActivos.map((eq) => (
                  <label
                    key={eq.id}
                    className="flex items-center gap-2 text-sm px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={f.equiposUsados.includes(eq.id)}
                      onChange={(e) => toggleEquipo(eq.id, e.target.checked)}
                    />
                    <span className="flex-1">{eq.nombre}</span>
                    <span className="text-slate-400 text-xs">{Number(eq.potenciaW)} W</span>
                  </label>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 mt-3">
              {input('luzMinutos', 'Minutos de uso', 'number', 'min')}
              <div className="rounded-md p-3 border bg-slate-50 border-slate-200">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Consumo</div>
                <div className="text-sm font-semibold mt-0.5 leading-tight">
                  {totalWatts} W · {luzKWh.toFixed(3)} kWh
                </div>
                {precioLuz > 0 ? (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    ≈ {cop(costoLuzPreview)}
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    (calculá para ver costo)
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mt-2 mb-2">Trabajo</h3>
            <div className="grid grid-cols-3 gap-3">
              {input('personas', 'Personas')}
              {input('horasTrabajoH', 'Horas', 'number', 'h')}
              {input('horasTrabajoMin', 'Minutos', 'number', 'min')}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Total: <b className="text-slate-700">{(toNum(f.horasTrabajoH) + toNum(f.horasTrabajoMin) / 60).toFixed(2)} h</b>
              {' '}× {f.personas || 0} persona(s)
            </div>
          </div>

          <div>
            <label className="label">Notas (opcional)</label>
            <textarea
              className="input min-h-[60px]"
              value={f.notas}
              onChange={(e) => setF((s) => ({ ...s, notas: e.target.value }))}
              placeholder="Observaciones del lote"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button type="button" onClick={calcular} disabled={calculando} className="btn-primary flex-1">
              {calculando ? 'Calculando…' : 'Calcular'}
            </button>
            <button
              type="button"
              onClick={guardarLote}
              disabled={guardando || !tiene('lotes:write')}
              className="btn-accent flex-1"
              title={!tiene('lotes:write') ? 'No tienes permiso para guardar lotes' : ''}
            >
              {guardando ? 'Guardando…' : '📌 Guardar en Producción del día'}
            </button>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
          {mensaje && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{mensaje}</div>}
        </div>

        {/* OUTPUT */}
        <div className="card p-4 sm:p-5 space-y-4">
          <h2 className="font-semibold">Resultado</h2>
          {!r && (
            <div className="text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded-md p-6 text-center">
              Ingresa los datos y pulsa <b>Calcular</b> para ver el resultado.
            </div>
          )}
          {r && loteSimulado && (
            <>
              <div className="flex items-center justify-between text-xs text-slate-500 -mt-2">
                <div>
                  <b className="text-slate-700">{num(r.arepasNetas, 0)}</b> arepas netas
                </div>
                <div>
                  <b className="text-slate-700">{num(r.paquetes, 0)}</b> paquetes · <b className="text-slate-700">{num(payload.kilosMaiz, 2)}</b> kg maíz
                </div>
              </div>

              <IndicadoresFinancieros
                lote={loteSimulado}
                margenObjetivo={margenObjetivo}
                distribucion={distribucionSimulada}
              />

              {precios && r.desglose && (
                <div className="pt-3 border-t border-slate-200">
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Desglose de costos del lote</h3>
                  <div className="text-sm space-y-1">
                    <LineaCosto
                      label="Maíz"
                      cantidad={num(payload.kilosMaiz, 2)}
                      unidad="kg"
                      precio={precios.maiz}
                      precioUnidad="kg"
                      total={r.desglose.maizCOP}
                    />
                    <LineaCosto
                      label="Gas"
                      cantidad={num(payload.gasLb, 2)}
                      unidad="lb"
                      precio={precios.gas}
                      precioUnidad="lb"
                      total={r.desglose.gasCOP}
                    />
                    <LineaCosto
                      label="Agua"
                      cantidad={num(payload.aguaL, 2)}
                      unidad="L"
                      precio={precios.agua}
                      precioUnidad="L"
                      total={r.desglose.aguaCOP}
                    />
                    <LineaCosto
                      label="Luz"
                      cantidad={num(luzKWh, 3)}
                      unidad="kWh"
                      precio={precios.luz}
                      precioUnidad="kWh"
                      total={r.desglose.luzCOP}
                    />
                    <LineaCosto
                      label="Bolsas"
                      cantidad={num(payload.bolsas, 0)}
                      unidad="und"
                      precio={precios.bolsa}
                      precioUnidad="und"
                      total={r.desglose.bolsasCOP}
                    />
                    <LineaCosto
                      label="Mano de obra"
                      cantidad={num(payload.personas * payload.horasTrabajo, 2)}
                      unidad="h"
                      precio={precios.manoObra}
                      precioUnidad="h"
                      total={r.desglose.manoObraCOP}
                    />
                    <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                      <span>Costo variable del lote</span>
                      <span>{cop(r.desglose.maizCOP + r.desglose.gasCOP + r.desglose.aguaCOP + r.desglose.luzCOP + r.desglose.bolsasCOP + r.desglose.manoObraCOP)}</span>
                    </div>
                  </div>
                </div>
              )}

              {fijos && (
                <div className="pt-3 border-t border-slate-200 text-xs text-slate-500 space-y-1">
                  <div>
                    Costo fijo/paq ={' '}
                    <b className="text-slate-700">
                      ({cop(fijos.depreciacionMensual)} depreciación
                      {fijos.costosFijosAdicionalesMensuales > 0
                        ? ` + ${cop(fijos.costosFijosAdicionalesMensuales)} fijos mensuales`
                        : ''}
                      )
                    </b>{' '}
                    ÷ <b className="text-slate-700">{fijos.paquetesPorMes}</b> paq/mes estimados.
                  </div>
                  <div>
                    Editá estos valores en{' '}
                    <Link to="/configuracion" className="text-sky-700 hover:underline">
                      Configuración
                    </Link>
                    {fijos.margenObjetivo ? (
                      <>
                        {' '}· Margen objetivo: <b className="text-slate-700">{pct(fijos.margenObjetivo)}</b>
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LineaCosto({
  label, cantidad, unidad, precio, precioUnidad, total,
}: {
  label: string; cantidad: string; unidad: string; precio: number; precioUnidad: string; total: number;
}) {
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-slate-600">
        {label}:{' '}
        <span className="text-slate-400">
          {cantidad} {unidad} × {cop(precio)}/{precioUnidad}
        </span>
      </span>
      <b className="text-slate-800">{cop(total)}</b>
    </div>
  );
}
